import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../assets/sclogo-250-1.svg';
import API_CONFIG from '../config/api';

/**
 * Configuration constants for the Login component
 */
const LOGIN_CONFIG = {
    // Email validation settings
    EMAIL_VALIDATION: {
        REGEX: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
        PERSONAL_DOMAINS: ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com']
    },
    
    // UI timing settings
    TIMING: {
        SUCCESS_MESSAGE_DURATION: 5000,
        DEBOUNCE_DELAY: 800,
        MIN_CHARS_FOR_VALIDATION: 3
    },
    
    // CSS classes for input states
    INPUT_STATES: {
        DEFAULT: 'border-gray-300 focus:ring-blue-500',
        ERROR: 'border-red-500 focus:ring-red-500',
        SUCCESS: 'border-green-500 focus:ring-green-500'
    }
};

/**
 * Utility functions for email validation and processing
 */
const EmailUtils = {
    /**
     * Validates email format using regex
     * @param {string} email - Email address to validate
     * @returns {boolean} True if email format is valid
     */
    isValidFormat(email) {
        return LOGIN_CONFIG.EMAIL_VALIDATION.REGEX.test(email);
    },

    /**
     * Checks if email domain is a personal domain
     * @param {string} email - Email address to check
     * @returns {boolean} True if domain is personal
     */
    isPersonalDomain(email) {
        const domain = email.split('@')[1]?.toLowerCase();
        return LOGIN_CONFIG.EMAIL_VALIDATION.PERSONAL_DOMAINS.includes(domain);
    },

    /**
     * Sanitizes email input by trimming and converting to lowercase
     * @param {string} email - Raw email input
     * @returns {string} Sanitized email
     */
    sanitize(email) {
        return email.trim().toLowerCase();
    },

    /**
     * Debounces function calls to limit execution rate
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} Debounced function
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
};

/**
 * Login Component
 * 
 * Handles user authentication via magic link email.
 * Features:
 * - Email validation with business domain preference
 * - Magic link generation and verification
 * - Responsive design with accessibility support
 * - Error handling and user feedback
 */
const Login = () => {
    // Component state
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // React Router hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    /**
     * Effect: Check for magic link token on component mount
     */
    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            verifyMagicLink(token);
        }
    }, [searchParams]);

    /**
     * Verifies magic link token and handles authentication
     * @param {string} token - Magic link token to verify
     */
    const verifyMagicLink = async (token) => {
        try {
            setIsLoading(true);
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VERIFY_TOKEN}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Store authentication token and user data if provided
                if (data.authToken) {
                    localStorage.setItem('authToken', data.authToken);
                }
                if (data.user) {
                    localStorage.setItem('userData', JSON.stringify(data.user));
                }
                navigate('/dashboard');
            } else {
                showError(data.message || 'Invalid or expired magic link');
            }
        } catch (error) {
            console.error('Token verification error:', error);
            showError('Failed to verify magic link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Validates email field and returns validation result
     * @param {string} emailValue - Email to validate
     * @param {boolean} showMessages - Whether to display validation messages
     * @returns {Object} Validation result with isValid and message
     */
    const validateEmailField = (emailValue, showMessages = true) => {
        const sanitizedEmail = EmailUtils.sanitize(emailValue);
        
        // Empty email is neutral state
        if (!sanitizedEmail) {
            if (showMessages) {
                clearMessages();
            }
            return { isValid: false, message: '' };
        }

        // Format validation
        if (!EmailUtils.isValidFormat(sanitizedEmail)) {
            if (showMessages && sanitizedEmail.length > 5) {
                showError('Please enter a valid email address');
            }
            return { isValid: false, message: 'Invalid email format' };
        }

        // Business domain preference
        if (EmailUtils.isPersonalDomain(sanitizedEmail)) {
            if (showMessages) {
                showError('Please use your business email address');
            }
            return { isValid: false, message: 'Personal email domain not allowed' };
        }

        // Valid email
        if (showMessages) {
            showSuccess('Email looks good!');
        }
        return { isValid: true, message: 'Valid email' };
    };

    /**
     * Debounced validation function to reduce UI jumpiness
     */
    const debouncedValidation = useCallback(
        EmailUtils.debounce((showMessages) => {
            if (email.length >= LOGIN_CONFIG.TIMING.MIN_CHARS_FOR_VALIDATION) {
                validateEmailField(email, showMessages);
            }
        }, LOGIN_CONFIG.TIMING.DEBOUNCE_DELAY),
        [email]
    );

    /**
     * Handles email input changes with silent validation
     * @param {Event} e - Input change event
     */
    const handleEmailInput = (e) => {
        const value = e.target.value;
        setEmail(value);
        
        // Clear messages and perform silent validation
        clearMessages();
        debouncedValidation(false);
    };

    /**
     * Handles email field blur with visible validation
     */
    const handleEmailBlur = () => {
        if (email.length >= LOGIN_CONFIG.TIMING.MIN_CHARS_FOR_VALIDATION) {
            validateEmailField(email, true);
        }
    };

    /**
     * Sends magic link email to the provided address
     * @param {string} emailAddress - Email address to send magic link to
     * @returns {Promise<Object>} API response with success status and message
     */
    const sendMagicLink = async (emailAddress) => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MAGIC_LINK}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailAddress })
            });

            const data = await response.json();

            return {
                success: response.ok && data.success,
                message: data.message || (response.ok ? 'Magic link sent successfully' : 'Failed to send magic link')
            };
        } catch (error) {
            console.error('Magic link request error:', error);
            return { 
                success: false, 
                message: 'Network error. Please try again.' 
            };
        }
    };

    /**
     * Handles form submission and magic link sending
     * @param {Event} e - Form submit event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const sanitizedEmail = EmailUtils.sanitize(email);
        const validation = validateEmailField(sanitizedEmail, true);
        
        if (!validation.isValid) {
            return;
        }

        setIsLoading(true);
        clearMessages();

        try {
            const result = await sendMagicLink(sanitizedEmail);
            
            if (result.success) {
                showSuccess('Magic link sent! Check your email and click the link to sign in.');
                setEmail('');
            } else {
                showError(result.message);
            }
        } catch (error) {
            console.error('Form submission error:', error);
            showError('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Displays error message with smooth transition
     * @param {string} message - Error message to display
     */
    const showError = (message) => {
        clearMessages();
        setErrorMessage(message);
    };

    /**
     * Displays success message with auto-hide timer
     * @param {string} message - Success message to display
     */
    const showSuccess = (message) => {
        clearMessages();
        setSuccessMessage(message);

        // Auto-hide success message
        setTimeout(() => {
            setSuccessMessage('');
        }, LOGIN_CONFIG.TIMING.SUCCESS_MESSAGE_DURATION);
    };

    /**
     * Clears all validation messages
     */
    const clearMessages = () => {
        setErrorMessage('');
        setSuccessMessage('');
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <main className="enterprise-card rounded-xl shadow-2xl border border-gray-100 py-12 px-8" role="main">
                    {/* Logo and Header Section */}
                    <header className="logo-container text-center">
                        <img 
                            src={logo}
                            alt="SureCost Logo" 
                            className="h-16 w-auto mx-auto mb-6"
                        />
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
                        <p className="text-lg text-gray-600">Sign in to your Vendor Dashboard</p>
                    </header>

                    {/* Login Form Section */}
                    <section className="form-section">
                        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                            <div>
                                <label 
                                    htmlFor="email" 
                                    className="block text-sm font-semibold text-gray-900 mb-2"
                                >
                                    Email Address
                                </label>
                                
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={handleEmailInput}
                                    onBlur={handleEmailBlur}
                                    className="block w-full"
                                    placeholder="Enter your business email"
                                    aria-describedby="email-error email-success"
                                    disabled={isLoading}
                                />
                                
                                {/* Validation Messages */}
                                <div className="message-container">
                                    {errorMessage && (
                                        <p 
                                            id="email-error" 
                                            className="message text-red-600 opacity-100"
                                            role="alert"
                                            aria-live="polite"
                                        >
                                            {errorMessage}
                                        </p>
                                    )}
                                    
                                    {successMessage && (
                                        <p 
                                            id="email-success" 
                                            className="message text-green-600 opacity-100"
                                            role="status"
                                            aria-live="polite"
                                        >
                                            {successMessage}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn-primary"
                                aria-describedby="submit-description"
                            >
                                {isLoading && (
                                    <svg 
                                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                                        fill="none" 
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle 
                                            className="opacity-25" 
                                            cx="12" 
                                            cy="12" 
                                            r="10" 
                                            stroke="currentColor" 
                                            strokeWidth="4"
                                        />
                                        <path 
                                            className="opacity-75" 
                                            fill="currentColor" 
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                )}
                                <span>
                                    {isLoading ? 'Sending secure sign-in link...' : 'Send secure sign-in link'}
                                </span>
                            </button>
                        </form>
                    </section>

                    {/* Footer Links Section */}
                    <footer className="footer-links text-center">
                        <p className="text-xs text-gray-500 mb-4">
                            By continuing, you agree to our{' '}
                            <a 
                                href="https://www.surecost.com/service-terms-conditions" 
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Terms and Conditions"
                            >
                                Terms and Conditions
                            </a>{' '}
                            and{' '}
                            <a 
                                href="https://app.websitepolicies.com/policies/view/iam8lalb" 
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Privacy Policy"
                            >
                                Privacy Policy
                            </a>
                        </p>
                        
                        <p className="text-sm text-gray-400">
                            Need help?{' '}
                            <a 
                                href="https://www.surecost.com/contact-support" 
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact Support"
                            >
                                Contact Support
                            </a>
                        </p>
                    </footer>
                </main>
            </div>
        </div>
    );
};

export default Login;